import React, { ChangeEvent } from "react";

import ColorPicker from "components/basic/color-picker/ColorPicker";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { Divider } from "primereact/divider";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { T } from "services/lang/LanguageService";
import { AdvancedProps, LineConfig, SparkLineProps } from "../sparkline/SparkLineTypes";

const options = [
  { label: T("word.none"), value: "none" },
  { label: T("word.dataset"), value: "dataset" },
  { label: T("word.constant"), value: "constant" },
]

type SparkLineAdvancedInputsProps = {
  currentConfig: SparkLineProps
  onDatasetChange: (value: React.SetStateAction<SparkLineProps>) => void;
};

const SparkLineAdvancedInputs: React.FC<SparkLineAdvancedInputsProps> = ({ 
  currentConfig,
  onDatasetChange,
 }) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement> | DropdownChangeEvent) => {
    onDatasetChange(prev => ({ ...prev, baseline: { ...prev.baseline, [e.target.name]: e.target.value } }))
  }
  
  return (
    <div className="grid col-12 border-1 border-gray-200 border-round p-3 ml-2 gap-3">
      <LineUsage
        label={T("label.baseline")}
        onDatasetChange={onDatasetChange}
        lineConfig={currentConfig.baseline}
        property="baseline"
      />

      <div className="w-5 md:w-3 mb-3">
        <label htmlFor="baselineWidth">{T("label.baseline-width")}</label>
        <InputNumber
          id="baselineWidth"
          name="width"
          value={currentConfig.baseline?.width}
          onValueChange={handleChange}
          useGrouping={false}
          suffix=" px"
          className="p-inputtext-sm w-full"
        />
      </div>

      <div className="w-max mb-3">
        <label htmlFor="fillAboveColor">{T("label.above-fill-color")}</label>
        <ColorPicker
          id="fillAboveColor"
          name="fillAboveColor"
          value={currentConfig.baseline?.fillAboveColor}
          onValueChange={handleChange}
        />
      </div>
      <div className="w-max mb-3">
        <label htmlFor="fillBelowColor">{T("label.below-fill-color")}</label>
        <ColorPicker
          id="fillBelowColor"
          name="fillBelowColor"
          value={currentConfig.baseline?.fillBelowColor}
          onValueChange={handleChange}
        />
      </div>
      <Divider />
      <LineUsage
        label={T("word.max")}
        onDatasetChange={onDatasetChange}
        lineConfig={currentConfig.maxLine}
        property="maxLine"
      />
      <Divider />
      <LineUsage
        label={T("word.min")}
        onDatasetChange={onDatasetChange}
        lineConfig={currentConfig.minLine}
        property="minLine"
      />
    </div>
  );
}

export default SparkLineAdvancedInputs;

type LineUsageProps = {
  lineConfig: LineConfig | undefined;
  property: keyof AdvancedProps;
  label: string;
  onDatasetChange: (value: React.SetStateAction<SparkLineProps>) => void;
};

const LineUsage: React.FC<LineUsageProps> = ({lineConfig, property, label, onDatasetChange}) => {
  const lineConfigChange = (key: keyof LineConfig, value?: number | string | null) => {
    onDatasetChange(prev => ({ ...prev, [property]: { ...lineConfig, [key]: value } }))
  }

  return (
    <div className="w-full flex align-items-center gap-3 flex-wrap md:flex-nowrap">
      <div className="flex flex-column mb-3">
        <label htmlFor="usage">{label}</label>
        <Dropdown
          id="usage"
          value={lineConfig?.usage}
          className="w-9rem p-inputtext-sm"
          options={options}
          onChange={e => lineConfigChange("usage", e.value)}
        />
      </div>
      <div className="w-max mb-3">
        <label htmlFor="color">{T("word.color")}</label>
        <ColorPicker
          id="color"
          value={lineConfig?.color}
          onValueChange={e => lineConfigChange("color", e.target.value)}
        />
      </div>
      {lineConfig?.usage === "constant" && (
        <div className="flex flex-column mb-3">
          <label htmlFor="constant">{T("word.constant")}</label>
          <InputNumber
            id="constant"
            value={lineConfig.constant}
            onValueChange={e => lineConfigChange("constant", e.target.value)}
            useGrouping={false}
            maxFractionDigits={2}
            className="p-inputtext-sm"
          />
        </div>
      )}
      {lineConfig?.usage === "dataset" && (
        <div className="w-full mb-3">
          <label htmlFor="baselineDataset">{T("label.baseline-dataset")}</label>
          <DatasetDropdown
            value={lineConfig.dataset}
            onChange={val => lineConfigChange("dataset", val)}
          />
        </div>
      )}
    </div>
  );
}